import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, ShoppingBag, Clock } from "lucide-react";
import api from "../lib/api";
import { useCart, gbp } from "../context/CartContext";
import { BrandLogo } from "../components/BrandLogo";
import ItemDrawer from "../components/ItemDrawer";
import BasketSheet from "../components/BasketSheet";
import { Badge } from "../components/ui/badge";

export default function MenuPage() {
  const navigate = useNavigate();
  const { mode, addLine, updateLine, count, subtotal, items } = useCart();
  const [menu, setMenu] = useState([]);
  const [settings, setSettings] = useState(null);
  const [activeCat, setActiveCat] = useState(null);
  const [drawerItem, setDrawerItem] = useState(null);
  const [editLine, setEditLine] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [basketOpen, setBasketOpen] = useState(false);
  const sectionRefs = useRef({});
  const chipBarRef = useRef(null);
  const chipRefs = useRef({});
  const clickScrolling = useRef(false);

  useEffect(() => {
    api.get("/menu").then((r) => { setMenu(r.data); if (r.data[0]) setActiveCat(r.data[0].id); });
    api.get("/settings/public").then((r) => setSettings(r.data));
  }, []);

  // Scrollspy: highlight the category whose section is currently near the top.
  useEffect(() => {
    if (menu.length === 0) return;
    const onScroll = () => {
      if (clickScrolling.current) return;
      const marker = 180; // just below the sticky header
      let current = menu[0]?.id;
      for (const cat of menu) {
        const el = sectionRefs.current[cat.id];
        if (el && el.getBoundingClientRect().top <= marker) current = cat.id;
      }
      setActiveCat((prev) => (prev === current ? prev : current));
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [menu]);

  // Keep the active chip visible in the horizontal bar.
  useEffect(() => {
    const chip = chipRefs.current[activeCat];
    if (chip) chip.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
  }, [activeCat]);

  const openItem = (item) => {
    setDrawerItem({ ...item, base_price: item.price });
    setEditLine(null);
    setDrawerOpen(true);
  };

  const openEdit = (line) => {
    const source = menu.flatMap((c) => c.items).find((i) => i.id === line.item_id);
    setDrawerItem({ ...(source || {}), base_price: line.base_price, name: line.name, extras: source?.extras || [] });
    setEditLine(line);
    setDrawerOpen(true);
  };

  const scrollTo = (cid) => {
    setActiveCat(cid);
    clickScrolling.current = true;
    sectionRefs.current[cid]?.scrollIntoView({ behavior: "smooth", block: "start" });
    setTimeout(() => { clickScrolling.current = false; }, 700);
  };

  const nudgeChips = (dir) => {
    chipBarRef.current?.scrollBy({ left: dir * 220, behavior: "smooth" });
  };

  const onChipWheel = useCallback((e) => {
    if (chipBarRef.current && Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
      chipBarRef.current.scrollLeft += e.deltaY;
    }
  }, []);

  return (
    <div className="min-h-screen bg-brand-cream grain pb-28">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-brand-dark text-brand-cream">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between">
          <button data-testid="back-home" onClick={() => navigate("/")} className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center active:scale-90 transition-transform">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <BrandLogo dark size="text-2xl" />
          <Badge className="bg-brand-teal text-white capitalize border-0">{mode}</Badge>
        </div>
        {/* Category chips */}
        <div className="max-w-lg mx-auto px-2 pb-3 flex items-center gap-1">
          <button data-testid="chip-scroll-left" onClick={() => nudgeChips(-1)}
            className="hidden md:flex shrink-0 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 items-center justify-center transition-colors">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div ref={chipBarRef} onWheel={onChipWheel} className="flex-1 flex gap-2 overflow-x-auto no-scrollbar scroll-smooth px-2">
            {menu.map((c) => (
              <button key={c.id} ref={(el) => (chipRefs.current[c.id] = el)} data-testid={`cat-chip-${c.id}`} onClick={() => scrollTo(c.id)}
                className={`whitespace-nowrap px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${activeCat === c.id ? "bg-brand-teal text-white" : "bg-white/10 text-brand-cream/70 hover:bg-white/20"}`}>
                {c.name}
              </button>
            ))}
          </div>
          <button data-testid="chip-scroll-right" onClick={() => nudgeChips(1)}
            className="hidden md:flex shrink-0 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 items-center justify-center transition-colors">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {settings?.store_status && !settings.store_status.open && (
        <div className="max-w-lg mx-auto px-4 pt-4">
          <div className="rounded-2xl bg-amber-50 border border-amber-200 p-3 flex gap-2 items-center">
            <Clock className="w-4 h-4 text-amber-600" />
            <p className="text-sm text-amber-800">{settings.store_status.message}{settings.store_status.next ? ` Next: ${settings.store_status.next}` : ""}</p>
          </div>
        </div>
      )}

      {/* Menu sections */}
      <div className="max-w-lg mx-auto px-4">
        {menu.map((cat) => (
          <div key={cat.id} ref={(el) => (sectionRefs.current[cat.id] = el)} className="pt-7 scroll-mt-32">
            <h2 className="font-head text-2xl font-bold text-brand-dark mb-3">{cat.name}</h2>
            <div className="space-y-3">
              {cat.items.map((item) => (
                <button
                  key={item.id}
                  data-testid={`menu-item-${item.id}`}
                  onClick={() => item.available && openItem(item)}
                  disabled={!item.available}
                  className={`w-full text-left flex gap-3 bg-white rounded-2xl p-3 border border-brand-dark/5 shadow-sm active:scale-[0.99] transition-transform ${!item.available ? "opacity-60" : ""}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-brand-dark">{item.name}</h3>
                      {!item.available && <Badge variant="outline" className="text-[10px] border-red-300 text-red-600 bg-red-50">Sold out</Badge>}
                    </div>
                    <p className="text-sm text-brand-dark/55 line-clamp-2 mt-0.5">{item.description}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="font-bold text-brand-teal">{gbp(item.price)}</span>
                      {item.allergens?.length > 0 && <span className="text-[11px] text-brand-dark/40">· {item.allergens.join(", ")}</span>}
                    </div>
                  </div>
                  {item.image_url && (
                    <div className="w-24 h-24 rounded-xl overflow-hidden shrink-0 relative">
                      <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                      {item.available && (
                        <span className="absolute bottom-1 right-1 w-7 h-7 rounded-full bg-brand-teal text-white flex items-center justify-center text-lg font-bold shadow">+</span>
                      )}
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Sticky basket bar */}
      {count > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-40 p-4" style={{ boxShadow: "0 -4px 20px rgba(0,0,0,0.08)" }}>
          <div className="max-w-lg mx-auto">
            <button data-testid="view-basket-bar" onClick={() => setBasketOpen(true)}
              className="w-full h-14 rounded-2xl bg-brand-teal hover:bg-brand-teal-dark text-white font-bold flex items-center justify-between px-5 shadow-xl active:scale-[0.99] transition-transform">
              <span className="flex items-center gap-2">
                <span className="w-7 h-7 rounded-full bg-white/25 flex items-center justify-center text-sm">{count}</span>
                <ShoppingBag className="w-5 h-5" /> View basket
              </span>
              <span>{gbp(subtotal)}</span>
            </button>
          </div>
        </div>
      )}

      <ItemDrawer open={drawerOpen} onOpenChange={setDrawerOpen} item={drawerItem}
        editLine={editLine} onAdd={addLine} onUpdate={updateLine} />
      <BasketSheet open={basketOpen} onOpenChange={setBasketOpen} settings={settings} onEdit={(l) => { setBasketOpen(false); openEdit(l); }} />
    </div>
  );
}
